import React, { AnimationEventHandler, createContext, ReactNode, useContext } from 'react';
import classnames from 'classnames';
import * as Dialog from '@radix-ui/react-dialog';
import Icon from './Icon';
import ScrollArea from './ScrollArea';
import { getFocusOutlineClass, getTextColorClass } from '@/utils/theme';
import PanelContentWithErrorBoundary from '../blocks/ErrorFallback/PanelContentWithErrorBoundary';

export interface PanelProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  size?: 'small' | 'medium';
  theme?: 'light' | 'dark';
  children: React.ReactNode;
  forceClose?: boolean;
  dataComponent?: string;
  preventOutsideClose?: boolean;
}

interface PanelContentProps {
  side?: 'left' | 'right';
  closeLabel?: string;
  children: React.ReactNode;
  className?: string;
  hasHorizontalPadding?: boolean;
  isL2Panel?: boolean;
  dimBackground?: boolean;
  onAnimationEnd?: AnimationEventHandler<HTMLDivElement>;
  onBackButtonPress?: () => void;
  leftHeaderContent?: React.ReactNode;
  centerHeaderContent?: React.ReactNode;
  isNestedModal?: boolean;
  customPanelId?: string;
  forceFocusOnTab?: boolean;
  miniPaddingHeader?: boolean;
  titleHeaderClassNames?: string;
  preventOutsideClose?: boolean;
}

interface PanelHeaderProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
  isL2Panel?: boolean;
}

interface PanelTitleProps {
  children?: React.ReactNode;
  hasHorizontalPadding?: boolean;
}

interface PanelBodyProps {
  children?: React.ReactNode;
  className?: string;
  hasHorizontalPadding?: boolean;
  scrollClassName?: string;
  theme?: 'light' | 'dark';
}

export interface PanelFooterProps {
  divider?: 'line' | 'gradient';
  children: React.ReactNode;
  className?: string;
}

interface PanelPrivateProps {
  Content: React.FC<PanelContentProps>;
  Header: React.FC<PanelHeaderProps>;
  Title: React.FC<PanelTitleProps>;
  Body: React.FC<PanelBodyProps>;
  Footer: React.FC<PanelFooterProps>;
  Trigger: typeof Dialog.Trigger;
}

const getHorizontalPadding = ({
  hasHorizontalPadding = true,
  size
}: {
  hasHorizontalPadding: PanelBodyProps['hasHorizontalPadding'];
  size?: PanelProps['size'];
}) => {
  return classnames({
    'px-6': hasHorizontalPadding,
    'md:px-8': size === 'small' && hasHorizontalPadding,
    'lg:px-20': size === 'medium' && hasHorizontalPadding
  });
};

const PanelContext = createContext<{ size?: string; theme?: string }>({});

const Panel: React.FC<PanelProps> & PanelPrivateProps = ({
  children,
  size = 'medium',
  theme = 'light',
  isOpen,
  dataComponent,
  onOpenChange,
  forceClose = false
}) => {
  return (
    <PanelContext.Provider value={{ size, theme }}>
      <Dialog.Root
        open={forceClose === true ? false : isOpen}
        onOpenChange={onOpenChange}
        data-component={dataComponent}
      >
        {children}
      </Dialog.Root>
    </PanelContext.Provider>
  );
};

const PanelContent: React.FC<PanelContentProps> = ({
  isL2Panel = false,
  side = 'right',
  closeLabel = 'Close',
  children,
  className,
  hasHorizontalPadding = true,
  dimBackground = true,
  onAnimationEnd,
  onBackButtonPress,
  leftHeaderContent,
  centerHeaderContent,
  isNestedModal = false,
  customPanelId,
  forceFocusOnTab,
  miniPaddingHeader,
  titleHeaderClassNames,
  preventOutsideClose = false,
  ...props
}) => {
  const { size, theme } = useContext(PanelContext);

  const findElementRecursively = (
    children: ReactNode,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _type: string | React.JSXElementConstructor<any>
  ):
    | string
    | number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | Iterable<React.ReactNode>
    | React.ReactPortal
    | undefined => {
    if (!children) return;

    for (const child of React.Children.toArray(children)) {
      if (!React.isValidElement(child)) continue;

      const element = child as React.ReactElement;

      // Handle both string and component constructor types
      if (typeof _type === 'string') {
        if (typeof element.type === 'string' && element.type === _type) {
          return element;
        }
      } else if (element.type === _type) {
        return element;
      }

      if (!!element?.props?.children && React.Children.count(element.props.children) > 0) {
        const found = findElementRecursively(element.props.children, _type);
        if (found) return found;
      }
    }
  };

  const contentClassName = classnames(
    'group fixed inset-y-0 size-full @container [contain:layout]',
    {
      'data-[state=open]:duration data-[state=closed]:duration fixed inset-y-0 size-full transition data-[state=open]:animate-in data-[state=closed]:animate-out':
        !isL2Panel,
      'data-[state=closed]:animate-[fadeOut_400ms_ease_forwards] data-[state=open]:animate-[fadeIn_400ms_ease_forwards]':
        isL2Panel
    },
    {
      'left-0 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left':
        side === 'left',
      'right-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right':
        side === 'right'
    },
    {
      'max-w-[422px]': size === 'small',
      'max-w-[768px] lg:max-w-[788px]': size === 'medium'
    },
    {
      'bg-white': theme === 'light',
      'bg-black': theme === 'dark'
    },
    {
      'z-modal': isNestedModal,
      'z-panel': !isNestedModal
    },
    className
  );

  const headerClassName = classnames('mb-10 flex items-center justify-between gap-6', {
    'px-6': !hasHorizontalPadding,
    'md:px-8': size === 'small' && !hasHorizontalPadding,
    'lg:px-20': size === 'medium' && !hasHorizontalPadding
  });

  const contentErrorBoundary = findElementRecursively(
    children,
    PanelContentWithErrorBoundary
  ) as React.ReactElement;
  const header = findElementRecursively(children, Panel.Header) as React.ReactElement;
  const body = (findElementRecursively(children, Panel.Body) as React.ReactElement) || (
    <Panel.Body />
  );
  const footer = findElementRecursively(children, Panel.Footer) as React.ReactElement;

  const bodyClassName = classnames({
    'opacity-0 group-data-[state=closed]:animate-panelL2SlideOut group-data-[state=open]:animate-panelL2SlideIn':
      isL2Panel
  });

  const newBodyChildrenClassNames = classnames(body.props.scrollClassName, {
    'h-full': !!contentErrorBoundary
  });

  const panelIdKey = customPanelId ? `-${customPanelId}` : '';

  let newBodyChildren = React.cloneElement(
    body,
    {
      ...body.props,
      hasHorizontalPadding,
      scrollClassName: newBodyChildrenClassNames,
      key: `panel${panelIdKey}`
    },
    [
      header ? (
        <div
          key={`panel-header${panelIdKey}`}
          className={headerClassName}
          data-component={'Panel-Header'}
        >
          {header}
        </div>
      ) : null,
      <div key={`panel-body${panelIdKey}`} className={bodyClassName} data-component="Panel-Body">
        {...React.Children.toArray((body as React.ReactElement).props?.children).map(
          (child, index) =>
            React.isValidElement(child)
              ? React.cloneElement(child, { key: `panel-body-child${panelIdKey}-${index}` })
              : child
        )}
      </div>
    ]
  );

  if (contentErrorBoundary) {
    newBodyChildren = React.cloneElement(
      contentErrorBoundary,
      { ...contentErrorBoundary.props, key: `panel-error-container${panelIdKey}` },
      [newBodyChildren, footer]
    );
  }

  const overlayClasses = classnames(
    'fixed inset-0 z-panel data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
    {
      'bg-black/30': dimBackground && !isL2Panel
    }
  );

  const hasOnlyCloseButton = !onBackButtonPress && !leftHeaderContent && !centerHeaderContent;

  const titleHeaderClassName = classnames(
    'mt-12 flex items-center',
    getHorizontalPadding({
      hasHorizontalPadding: true,
      size: size as PanelProps['size']
    }),
    {
      'justify-end': hasOnlyCloseButton,
      'justify-between': !hasOnlyCloseButton
    },
    titleHeaderClassNames,
    getTextColorClass(theme),
    getFocusOutlineClass(theme),
    { '!m-0 !p-8': miniPaddingHeader }
  );

  return (
    <Dialog.Portal>
      <Dialog.Overlay className={overlayClasses} />

      <Dialog.Content
        onAnimationEnd={onAnimationEnd}
        onInteractOutside={event => {
          if (preventOutsideClose) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={event => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-component="Toast"]')) {
            event.preventDefault();
          }
        }}
        className={contentClassName}
        {...props}
        {...(customPanelId
          ? {
              id: customPanelId,
              onCloseAutoFocus(event) {
                const target = event.target as HTMLElement;
                const targetId = target.getAttribute('id');
                const triggerElement: HTMLElement | null = document.querySelector(
                  `[aria-controls="${targetId}"]`
                );

                if (triggerElement) {
                  triggerElement.focus();
                }
              }
            }
          : {})}
        {...(forceFocusOnTab
          ? {
              onFocusCapture: event => {
                const target = event.target as HTMLElement;

                if (target.getAttribute('role') === 'dialog') {
                  if (target.getAttribute('data-state') === 'open') {
                    const isTargetFocused = target.matches(':focus-visible');
                    const panelBody = target.querySelector('[data-panel-body]');

                    setTimeout(() => {
                      if (isTargetFocused && panelBody) {
                        if (customPanelId) {
                          const element = document.querySelector(
                            `[aria-controls="${customPanelId}"]`
                          ) as HTMLElement;

                          if (element) {
                            element.focus();
                          }
                        } else {
                          const firstFocusableElement = panelBody.querySelector(
                            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                          ) as HTMLElement;

                          if (firstFocusableElement) {
                            firstFocusableElement.focus();
                          }
                        }
                      }
                    });
                  }
                }
              }
            }
          : {})}
      >
        <div className="relative flex h-full flex-col has-[[data-error-boundary-fallback]]:bg-gray-lightest">
          <div className={titleHeaderClassName}>
            {onBackButtonPress && (
              <button
                type="button"
                data-component="Panel-Previous-Button"
                onClick={onBackButtonPress}
                aria-label="previous"
                className={getFocusOutlineClass(theme)}
              >
                <Icon name="chevron-left" size="large" />
              </button>
            )}
            {leftHeaderContent}
            {centerHeaderContent}
            <Dialog.Close
              aria-label={closeLabel}
              className={getFocusOutlineClass(theme)}
              data-component="Panel-Close-Button"
            >
              <Icon name="close" size="large" />
            </Dialog.Close>
          </div>
          {newBodyChildren}
          {!contentErrorBoundary && footer}
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );
};

const PanelHeader: React.FC<PanelHeaderProps> = ({ isL2Panel, children, className, id }) => {
  const { theme } = useContext(PanelContext);

  const headingClassName = classnames(
    'heading-6-medium flex min-h-6 items-center lg:min-h-[26px]',
    getTextColorClass(theme),
    {
      'group-data-[state=closed]:animate-[fadeOut_400ms_ease_forwards] group-data-[state=open]:animate-[fadeIn_400ms_ease_100ms_forwards]':
        isL2Panel
    },
    className
  );

  return (
    <div className={headingClassName} id={id}>
      {children}
    </div>
  );
};

const PanelTitle: React.FC<PanelTitleProps> = ({ children, hasHorizontalPadding = true }) => {
  const { size } = useContext(PanelContext);
  const headingClassName = classnames(
    'heading-6-medium mb-8 text-black',
    getHorizontalPadding({
      hasHorizontalPadding,
      size: size as PanelProps['size']
    })
  );
  return (
    <div className={headingClassName} data-component="Panel-Title">
      {children}
    </div>
  );
};

const PanelBody: React.FC = ({
  children,
  hasHorizontalPadding = true,
  theme,
  className,
  scrollClassName
}: PanelBodyProps) => {
  const { size } = useContext(PanelContext);

  const scrollAreaClassName = classnames('h-full', getTextColorClass(theme), className);

  const scrollContentClassName = classnames(
    'pt-10',
    getHorizontalPadding({ hasHorizontalPadding, size: size as PanelProps['size'] }),
    scrollClassName
  );

  return (
    <div
      data-panel-body
      className={classnames('flex-1 overflow-auto')}
      data-component={'Panel-Body'}
    >
      <ScrollArea className={scrollAreaClassName}>
        <div className={scrollContentClassName}>{children}</div>
      </ScrollArea>
    </div>
  );
};

const PanelFooter: React.FC<PanelFooterProps> = ({ divider, children, className }) => {
  const { size, theme } = useContext(PanelContext);

  let dividerStyles = '';

  if (divider === 'line') {
    dividerStyles = classnames(
      'before:absolute before:inset-x-6 before:top-0 before:block before:h-px before:bg-gray-light before:content-[""]',
      {
        'before:md:inset-x-8': size === 'small',
        'before:lg:inset-x-20': size === 'medium'
      }
    );
  }

  if (divider === 'gradient') {
    dividerStyles = classnames(
      'pt-0',
      'before:pointer-events-none before:absolute before:-top-6 before:left-0 before:h-6 before:w-full before:content-[""]',
      'before:bg-gradient-to-b before:from-transparent before:to-white'
    );
  }

  const footerClassName = classnames(
    'relative px-6 py-5',
    {
      'md:px-8': size === 'small',
      'lg:px-20': size === 'medium'
    },
    getTextColorClass(theme),
    dividerStyles,
    className
  );

  return <div className={footerClassName}>{children}</div>;
};

Panel.Content = PanelContent;
Panel.Header = PanelHeader;
Panel.Title = PanelTitle;
Panel.Body = PanelBody;
Panel.Footer = PanelFooter;
Panel.Trigger = Dialog.Trigger;

export default Panel;
